import { Color } from "@repo/app-types/constants/colors";
import {
  getPassScoreColor,
  getPassScoreLabel,
} from "@repo/app-types/constants/passScore";
import {
  RecordingSpeakerRoleType,
  type RecordingMetadataSpeaker,
  type RecordingRawTranscriptFragment,
} from "@repo/app-types/entities/recording";
import {
  transcriptSearchMatchSchema,
  type AccountGradingRecording,
  type GradingId,
  type TranscriptSearchMatch,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { parseGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { buildPersonName } from "@repo/app-types/helpers/utilities";
import { getPillColor, Pill } from "@repo/ui/Pills/pill";
import { Size } from "@repo/ui/helpers/helpers";
import { fuzzySearch } from "levenshtein-search";
import { useMemo, type ReactNode } from "react";
import { z } from "zod";
import {
  AssessmentScore,
  LabeledAssessmentScore,
} from "../insights/assessmentScore.js";

export function useSortedRecordings(
  recordings: readonly AccountGradingRecording[] | undefined,
) {
  return useMemo(() => {
    if (!recordings) {
      return [];
    }

    return recordings.toSorted((r1, r2) => {
      if (r1.recorded_at === r2.recorded_at) {
        return 0;
      } else if (r1.recorded_at > r2.recorded_at) {
        return 1;
      } else {
        return -1;
      }
    });
  }, [recordings]);
}

/**
 * Modal path for opening a grading. When a transcript search is active and the
 * grading has a matched snippet, deep-links into the Transcript tab — the
 * match highlights travel via {@link buildTranscriptSearchNavState} navigation
 * state. Otherwise opens the default Assessment tab.
 */
export function buildGradingModalPath(
  gradingId: GradingId,
  searchQuery: string | undefined,
  snippet: TranscriptSearchMatch | undefined,
): string {
  const base = encodeURIComponent(gradingId);
  if (searchQuery?.trim() && snippet) {
    return `${base}/transcript`;
  }
  return `${base}/assessment`;
}

export interface TranscriptSearchNavState {
  query: string;
  matches: readonly TranscriptSearchMatch[];
}

const transcriptSearchNavStateSchema = z.object({
  transcriptSearch: z.object({
    query: z.string(),
    matches: z.array(transcriptSearchMatchSchema),
  }),
});

export function parseTranscriptSearchNavState(
  state: unknown,
): TranscriptSearchNavState | undefined {
  const parsed = transcriptSearchNavStateSchema.safeParse(state);
  return parsed.success ? parsed.data.transcriptSearch : undefined;
}

/**
 * The `modalNavigation.push` extra-state payload that
 * {@link parseTranscriptSearchNavState} reads back on the transcript page.
 */
export function buildTranscriptSearchNavState(
  query: string,
  matches: readonly TranscriptSearchMatch[],
): { transcriptSearch: TranscriptSearchNavState } {
  return { transcriptSearch: { query, matches } };
}

export function renderSpeakerName(
  speakers: readonly RecordingMetadataSpeaker[] | null | undefined,
  speakerId: string | undefined,
): ReactNode {
  const speaker =
    speakerId !== undefined
      ? speakers?.find((s) => s.id === speakerId)
      : undefined;

  return buildPersonName(speaker) || speaker?.name || "Unknown speaker";
}

const speakerBubbleClassNames: Record<RecordingSpeakerRoleType, string> = {
  [RecordingSpeakerRoleType.Representative]: "bg-blue-50",
  [RecordingSpeakerRoleType.Customer]: "bg-amber-50",
  [RecordingSpeakerRoleType.Other]: "bg-gray-50",
};

export function findSpeakerRole(
  speakers: readonly RecordingMetadataSpeaker[] | null | undefined,
  speakerId: string,
): RecordingSpeakerRoleType {
  const speaker = speakers?.find((s) => s.id === speakerId);
  if (!speaker || !speaker.role_type) {
    return RecordingSpeakerRoleType.Other;
  }

  return speaker.role_type;
}

export function buildSpeakerBubbleClassName(
  speakers: readonly RecordingMetadataSpeaker[] | null | undefined,
  speakerId: string | undefined,
): string {
  const speaker =
    speakerId !== undefined
      ? speakers?.find((s) => s.id === speakerId)
      : undefined;

  const roleType = speaker?.role_type;
  const fallbackRoleType = RecordingSpeakerRoleType.Other;

  // Double fallback just in case the role type doesn't fit the enum
  return (
    speakerBubbleClassNames[roleType ?? fallbackRoleType] ??
    speakerBubbleClassNames[fallbackRoleType]
  );
}

export type RecordingFragment = RecordingRawTranscriptFragment;

export interface RecordingCitation {
  start_time: number;
  text: string;
}

interface RecordingCitationWithFragment {
  speaker_id: string | undefined;
  start_time: number;
  text_before: string | undefined;
  text: string;
  text_after: string | undefined;
}

export function findFragmentForCitation(
  fragments: readonly RecordingFragment[],
  citation: RecordingCitation,
): RecordingCitationWithFragment {
  let bestResult: RecordingCitationWithFragment = {
    speaker_id: undefined,
    start_time: citation.start_time,
    text_before: undefined,
    text: citation.text,
    text_after: undefined,
  };
  let bestDist = Infinity;

  // Find the nearest three fragments around the citation start time, since the time might be slightly off due to rounding
  const aroundFragmentIndex = findFragmentIndexAtTime(
    fragments,
    citation.start_time,
  );
  const firstFragmentIndex = Math.max(0, aroundFragmentIndex - 1);
  const lastFragmentIndex = Math.min(
    aroundFragmentIndex + 1,
    fragments.length - 1,
  );

  // Fuzzy match with an edit distance of 10% of the citation text length, within reasonable runtime bounds
  const maxDist = Math.max(
    1,
    Math.min(Math.round(0.1 * citation.text.length), 16),
  );

  // Iterate through those fragments
  for (
    let fragmentIndex = firstFragmentIndex;
    fragmentIndex <= lastFragmentIndex;
    fragmentIndex++
  ) {
    // There's nothing better to find
    if (bestDist === 0) {
      break;
    }

    const fragment = fragments.at(fragmentIndex);
    if (!fragment) {
      continue;
    }

    // Iterate through matches
    for (const match of fuzzySearch(
      citation.text.toLowerCase(),
      fragment.text_transcript.toLowerCase(),
      maxDist,
    )) {
      // Is this a better match than what we already have?
      const matchLength = match.end - match.start;
      if (
        match.dist < bestDist ||
        (match.dist === bestDist && matchLength > bestResult.text.length)
      ) {
        bestResult = {
          speaker_id: fragment.speaker_id,
          start_time: citation.start_time,
          text_before: fragment.text_transcript.slice(0, match.start),
          text: fragment.text_transcript.slice(match.start, match.end),
          text_after: fragment.text_transcript.slice(match.end),
        };
        bestDist = match.dist;

        if (bestDist === 0) {
          break;
        }
      }
    }
  }

  return bestResult;
}

export function findFragmentIndexAtTime(
  fragments: readonly RecordingFragment[],
  time: number,
): number {
  let left = 0;
  let right = fragments.length - 1;

  // Use a modified binary search to find the nearest fragment
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const fragment = fragments[mid];
    if (fragment === undefined) {
      break;
    }

    if (fragment.start_time <= time && time < fragment.end_time) {
      return mid;
    } else if (fragment.end_time <= time) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return left;
}

// TODO: Implement separate functions for this.
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options: { plaintext: true; showLabel?: boolean },
): string;
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options?: { plaintext?: false; showLabel?: boolean },
): ReactNode;
export function renderGradingValue(
  valueType: "yes_no" | "1_5_score",
  value: string | undefined,
  options?: { plaintext?: boolean; showLabel?: boolean },
): string | ReactNode {
  const gradingValue = parseGradingValue(valueType, value);

  if (gradingValue === undefined) {
    return options?.plaintext ? (
      "--"
    ) : (
      <span className="text-muted-foreground">--</span>
    );
  }

  if (gradingValue.value === null) {
    return options?.plaintext ? (
      "N/A"
    ) : (
      <Pill size={Size.SMALL} color={Color.Gray} label="N/A" />
    );
  }

  if (gradingValue.type === "yes_no") {
    const label = getPassScoreLabel(gradingValue.value);
    return options?.plaintext ? (
      label
    ) : (
      <Pill
        size={Size.SMALL}
        color={getPillColor(getPassScoreColor(gradingValue.value))}
        label={label}
      />
    );
  }

  if (gradingValue.type === "1_5_score") {
    if (options?.plaintext) {
      return `${gradingValue.value}/5`;
    }

    return (options?.showLabel ?? true) ? (
      <LabeledAssessmentScore size={Size.SMALL} score={gradingValue.value} />
    ) : (
      <AssessmentScore size={Size.SMALL} score={gradingValue.value} />
    );
  }

  return options?.plaintext ? (
    "--"
  ) : (
    <span className="text-muted-foreground">--</span>
  );
}
